// Admin-only inbox: every user the admin has ever messaged, newest first.
// Tapping a row opens the same AdminMessageUserDialog used from /admin/users
// so the admin can view + reply to the thread inline.
import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Mail, ShieldCheck, Megaphone } from "lucide-react";
import AdminMessageUserDialog from "@/components/AdminMessageUserDialog";
import AdminBroadcastDialog from "@/components/AdminBroadcastDialog";
import { Button } from "@/components/ui/button";

function relTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function AdminInbox() {
  const { user, loading: authLoading } = useAuth();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [target, setTarget] = useState(null);
  const [broadcastOpen, setBroadcastOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/message-threads");
      setThreads(data.items || []);
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          err.message ||
          "Could not load conversations."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login?next=/admin/inbox" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;

  const totalUnread = threads.reduce((n, t) => n + (t.unread || 0), 0);

  return (
    <div
      className="mx-auto max-w-4xl px-4 sm:px-6 py-8 sm:py-12"
      data-testid="admin-inbox-page"
    >
      <div className="flex items-start gap-4">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
          style={{
            backgroundColor: "rgba(107,47,186,0.10)",
            color: "var(--avt-purple)",
          }}
        >
          <ShieldCheck size={22} />
        </div>
        <div className="flex-1">
          <div
            className="text-[10px] uppercase tracking-widest"
            style={{ color: "var(--terracotta)" }}
          >
            Admin messages
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight">
            Your conversations
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--ink-soft)" }}>
            Every user you've messaged, plus everyone who's replied. Tap a row
            to see the thread and reply in-place.
            {totalUnread > 0 && (
              <span
                className="ml-2 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                style={{
                  backgroundColor: "rgba(201,67,42,0.15)",
                  color: "var(--terracotta)",
                }}
                data-testid="admin-inbox-unread"
              >
                {totalUnread} new
              </span>
            )}
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setBroadcastOpen(true)}
          data-testid="admin-broadcast-open"
          className="rounded-full inline-flex items-center gap-2"
          style={{ backgroundColor: "var(--terracotta)", color: "#fff" }}
        >
          <Megaphone size={14} /> Broadcast
        </Button>
      </div>

      {error && (
        <div
          data-testid="admin-inbox-error"
          className="mt-6 text-sm rounded-md p-3"
          style={{
            backgroundColor: "rgba(201,67,42,0.08)",
            color: "var(--terracotta)",
          }}
        >
          {error}
        </div>
      )}

      <div
        className="mt-6 rounded-lg border overflow-hidden"
        style={{
          borderColor: "rgba(0,0,0,0.08)",
          backgroundColor: "#fff",
        }}
      >
        {loading ? (
          <div
            data-testid="admin-inbox-loading"
            className="py-16 text-center text-sm"
            style={{ color: "var(--ink-soft)" }}
          >
            <Loader2 size={14} className="animate-spin inline mr-2" />
            Loading conversations…
          </div>
        ) : threads.length === 0 ? (
          <div
            data-testid="admin-inbox-empty"
            className="py-16 px-6 text-center text-sm space-y-2"
            style={{ color: "var(--ink-soft)" }}
          >
            <Mail size={22} className="inline" />
            <p>
              You haven't messaged anyone yet. Head to{" "}
              <a
                href="/admin/users"
                className="underline"
                style={{ color: "var(--terracotta)" }}
              >
                /admin/users
              </a>{" "}
              and tap "Message" on any user to start a conversation.
            </p>
          </div>
        ) : (
          <ul>
            {threads.map((t) => (
              <li key={t.user_id}>
                <button
                  type="button"
                  data-testid={`admin-inbox-thread-${t.user_id}`}
                  onClick={() =>
                    setTarget({
                      id: t.user_id,
                      name: t.name,
                      email: t.email,
                      business_name: t.business_name,
                    })
                  }
                  className="w-full text-left border-t px-4 py-3 hover:bg-black/[0.02] transition-colors"
                  style={{ borderColor: "rgba(0,0,0,0.06)" }}
                >
                  <div className="flex items-center gap-3">
                    {t.avatar_url ? (
                      <img
                        src={t.avatar_url}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-serif"
                        style={{
                          backgroundColor: "var(--sand)",
                          color: "var(--terracotta)",
                        }}
                      >
                        {(t.name || t.email || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-medium truncate">
                          {t.name || t.email}
                        </div>
                        {t.role && (
                          <span
                            className="text-[10px] uppercase tracking-widest"
                            style={{ color: "var(--ink-soft)" }}
                          >
                            · {t.role}
                          </span>
                        )}
                        {t.unread > 0 && (
                          <span
                            className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                            style={{
                              backgroundColor: "var(--terracotta)",
                              color: "#fff",
                            }}
                          >
                            {t.unread} NEW
                          </span>
                        )}
                        <span
                          className={`text-[11px] shrink-0 ${
                            t.unread > 0 ? "" : "ml-auto"
                          }`}
                          style={{ color: "var(--ink-soft)" }}
                        >
                          {relTime(t.last_message_at)}
                        </span>
                      </div>
                      <div
                        className="text-sm truncate mt-0.5"
                        style={{ color: "var(--ink-soft)" }}
                      >
                        {t.last_sender_role === "admin" ? "You: " : ""}
                        {t.last_body || t.last_subject}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AdminMessageUserDialog
        user={target}
        open={!!target}
        onOpenChange={(next) => {
          if (!next) {
            setTarget(null);
            load(); // refresh so unread counts + snippets stay live
          }
        }}
      />

      <AdminBroadcastDialog
        open={broadcastOpen}
        onOpenChange={setBroadcastOpen}
        onSent={load}
      />
    </div>
  );
}
